import { useEffect, useState } from 'react';
import type {
  InfoSecurityIncidentPayload,
  InfoSecurityIncidentSeverity,
  InfoSecurityIncidentType,
} from './types';
import { INCIDENT_TYPE_LABEL, SEVERITY_LABEL } from './incidentsUtils';

const TYPES = Object.keys(INCIDENT_TYPE_LABEL) as InfoSecurityIncidentType[];
const SEVERITIES = Object.keys(SEVERITY_LABEL) as InfoSecurityIncidentSeverity[];

interface IncidentFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: InfoSecurityIncidentPayload) => Promise<void>;
  initial?: Partial<InfoSecurityIncidentPayload>;
  title: string;
  showInvestigation?: boolean;
}

function toLocalInput(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function defaultPayload(): InfoSecurityIncidentPayload {
  const now = new Date();
  const iso = now.toISOString();
  return {
    detectedAt: iso,
    reportedAt: iso,
    type: 'other',
    severity: 'low',
    description: '',
  };
}

export function IncidentFormModal({
  open,
  onClose,
  onSubmit,
  initial,
  title,
  showInvestigation = false,
}: IncidentFormModalProps) {
  const [form, setForm] = useState<InfoSecurityIncidentPayload>(defaultPayload());
  const [rootCauseText, setRootCauseText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm({ ...defaultPayload(), ...initial });
    const analysis = initial?.rootCauseAnalysis;
    if (analysis && typeof analysis === 'object' && 'summary' in analysis) {
      setRootCauseText(String((analysis as { summary?: string }).summary ?? ''));
    } else {
      setRootCauseText('');
    }
    setError(null);
  }, [open, initial]);

  if (!open) return null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload: InfoSecurityIncidentPayload = { ...form };
      if (showInvestigation) {
        payload.rootCauseAnalysis = rootCauseText.trim()
          ? { summary: rootCauseText.trim().slice(0, 10_000) }
          : null;
      }
      await onSubmit(payload);
      onClose();
    } catch {
      setError('No se pudo guardar el incidente.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4 sm:p-6">
      <div className="flex min-h-full items-start justify-center">
        <form
          className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto p-6 space-y-4 my-2 sm:my-4 shrink-0"
          onSubmit={handleSubmit}
        >
        <h2 className="text-lg font-semibold">{title}</h2>
        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block text-sm space-y-1">
            <span>Detectado</span>
            <input
              type="datetime-local"
              className="input w-full"
              required
              value={toLocalInput(form.detectedAt)}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  detectedAt: new Date(e.target.value).toISOString(),
                }))
              }
            />
          </label>
          <label className="block text-sm space-y-1">
            <span>Reportado</span>
            <input
              type="datetime-local"
              className="input w-full"
              required
              value={toLocalInput(form.reportedAt)}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  reportedAt: new Date(e.target.value).toISOString(),
                }))
              }
            />
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block text-sm space-y-1">
            <span>Tipo</span>
            <select
              className="input w-full"
              value={form.type}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, type: e.target.value as InfoSecurityIncidentType }))
              }
            >
              {TYPES.map((type) => (
                <option key={type} value={type}>
                  {INCIDENT_TYPE_LABEL[type]}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm space-y-1">
            <span>Severidad</span>
            <select
              className="input w-full"
              value={form.severity}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  severity: e.target.value as InfoSecurityIncidentSeverity,
                }))
              }
            >
              {SEVERITIES.map((sev) => (
                <option key={sev} value={sev}>
                  {SEVERITY_LABEL[sev]}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="block text-sm space-y-1">
          <span>Activos afectados</span>
          <textarea
            className="input w-full min-h-[72px]"
            value={form.affectedAssets ?? ''}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, affectedAssets: e.target.value || null }))
            }
            placeholder="Sistemas, bases de datos, servicios o información comprometida…"
          />
        </label>

        <label className="block text-sm space-y-1">
          <span>Descripción del incidente</span>
          <textarea
            className="input w-full min-h-[96px]"
            required
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
            placeholder="Describa qué ocurrió, cómo se detectó y el impacto inicial…"
          />
        </label>

        <label className="block text-sm space-y-1">
          <span>Acciones inmediatas</span>
          <textarea
            className="input w-full min-h-[72px]"
            value={form.immediateActions ?? ''}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, immediateActions: e.target.value || null }))
            }
            placeholder="Aislamiento, revocación de accesos, notificación interna…"
          />
        </label>

        {showInvestigation && (
          <label className="block text-sm space-y-1">
            <span>Análisis de causa raíz</span>
            <textarea
              className="input w-full min-h-[96px]"
              value={rootCauseText}
              onChange={(e) => setRootCauseText(e.target.value)}
              placeholder="Conclusiones del análisis de causa raíz…"
            />
          </label>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn btn-outline" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
        </form>
      </div>
    </div>
  );
}
